<template>
  <form class="interactive-social-wall__comment-form" @submit.prevent="submitComment">
    <label for="interactive-social-wall-comment-input">{{ $t('general.your_comment') }}</label>

    <div class="interactive-social-wall__comment-editor">
      <SocialWallAvatar :name="currentUserName" :photo="currentUserPhoto"
                        class="interactive-social-wall__comment-current-avatar"/>

      <textarea id="interactive-social-wall-comment-input"
                ref="commentInput"
                v-model="commentDraft"
                :disabled="busy"
                maxlength="1000"
                :placeholder="$t('general.write_your_comment')"
                rows="3"
                @input="clearCommentError"
                @keydown.ctrl.enter.prevent="submitComment"
                @keydown.meta.enter.prevent="submitComment"
      />
    </div>

    <p v-if="error" class="interactive-social-wall__comment-form-error" role="alert">
      <i aria-hidden="true" class="uil-exclamation-circle"/>
      {{ error }}
    </p>

    <div class="interactive-social-wall__comment-form-footer">
      <div>
        <button :disabled="busy"
                class="interactive-social-wall__comment-cancel"
                type="button"
                @click="$emit('close')"
        >
          {{ $t('general.cancel') }}
        </button>
        <button :aria-busy="submitting"
                :disabled="!canSubmitComment"
                class="interactive-social-wall__comment-submit"
                type="submit"
        >
          <span v-if="submitting"
                aria-hidden="true"
                class="interactive-social-wall__inline-spinner interactive-social-wall__inline-spinner--light"
          />
          {{ submitting ? $t('general.sending') : $t('general.submit_comment') }}
        </button>
      </div>
    </div>
  </form>
</template>

<script>
import UserProfileAvatar from "../../../../components/common/UserProfileAvatar.vue";
import { userName, userPhoto } from "../utils/presentation";

export default {
  name: "SocialWallCommentForm",
  components: {
    SocialWallAvatar: UserProfileAvatar,
  },
  props: {
    currentUser: {
      type: Object,
      default: () => ({}),
    },
    busy: {
      type: Boolean,
      default: false,
    },
    submitting: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      commentDraft: "",
    };
  },
  computed: {
    currentUserName() {
      return userName(this.currentUser, this.$t('general.you'));
    },
    currentUserPhoto() {
      return userPhoto(this.currentUser);
    },
    canSubmitComment() {
      return Boolean(this.commentDraft.trim()) && !this.busy && !this.loading;
    },
  },
  watch: {
    submitting(isSubmitting, wasSubmitting) {
      if (!wasSubmitting || isSubmitting || this.error) return;
      this.commentDraft = "";
      this.$nextTick(this.focusCommentInput);
    },
  },
  methods: {
    clearCommentError() {
      if (this.error) this.$emit("clear-comment-error");
    },
    submitComment() {
      const text = this.commentDraft.trim();
      if (!text || !this.canSubmitComment) return;
      this.$emit("submit-comment", text);
    },
    focusCommentInput() {
      this.$refs.commentInput?.focus();
    },
    resetForm() {
      this.commentDraft = "";
    },
  },
};
</script>

<style scoped>
.interactive-social-wall__comment-form,
.interactive-social-wall__comment-form * {
  box-sizing: border-box;
}

.interactive-social-wall__comment-form button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  color: inherit;
  font: inherit;
  line-height: 1;
  cursor: pointer;
  background: transparent;
  border: 0;
  outline: none;
  box-shadow: none;
}

.interactive-social-wall__comment-form {
  padding: 16px 18px 18px;
}

.interactive-social-wall__comment-form > label {
  display: block;
  margin-bottom: 8px;
  color: #303846;
  font-size: 10.5px;
  font-weight: 700;
  line-height: 1.4;
}

.interactive-social-wall__comment-editor {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.interactive-social-wall__comment-current-avatar {
  display: inline-flex;
  flex: 0 0 34px;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  overflow: hidden;
  font-size: 11px;
  font-weight: 600;
  border-radius: 50%;
}

.interactive-social-wall__comment-editor textarea {
  flex: 1;
  min-width: 0;
  min-height: 76px;
  padding: 10px 11px;
  color: #202734;
  font-family: var(--font-primary, "Poppins", sans-serif);
  font-size: 11px;
  font-weight: 400;
  line-height: 1.55;
  resize: vertical;
  background: #ffffff;
  border: 1px solid #d9e0e8;
  border-radius: 7px;
  outline: none;
  transition: border-color 160ms ease, box-shadow 160ms ease;
}

.interactive-social-wall__comment-editor textarea::placeholder {
  color: #929baa;
}

.interactive-social-wall__comment-editor textarea:focus {
  border-color: #72c861;
  box-shadow: 0 0 0 3px rgba(80, 184, 62, 0.12);
}

.interactive-social-wall__comment-editor textarea:disabled {
  cursor: wait;
  opacity: 0.7;
  background: #f7f8fa;
}

.interactive-social-wall__comment-form-error {
  --iconColor: #d84f4f;
  display: flex;
  align-items: flex-start;
  gap: 5px;
  margin: 8px 0 0 44px;
  color: #c74444;
  font-size: 9px;
  line-height: 1.4;
}

.interactive-social-wall__comment-form-error i {
  flex: 0 0 auto;
  margin-top: 1px;
  font-size: 12px;
}

.interactive-social-wall__comment-form-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 13px;
  padding-left: 44px;
}

.interactive-social-wall__comment-form-footer > div {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 8px;
}

.interactive-social-wall__comment-cancel,
.interactive-social-wall__comment-submit {
  min-height: 34px;
  padding: 0 13px !important;
  font-size: 10px !important;
  font-weight: 600 !important;
  border-radius: 6px;
}

.interactive-social-wall__comment-cancel {
  color: #68758b !important;
  background: #f4f6f8 !important;
  border: 1px solid #e2e6eb !important;
}

.interactive-social-wall__comment-submit {
  color: #ffffff !important;
  background: #50b83e !important;
  border: 1px solid #50b83e !important;
  box-shadow: 0 4px 10px rgba(80, 184, 62, 0.18) !important;
}

.interactive-social-wall__comment-submit:disabled,
.interactive-social-wall__comment-cancel:disabled {
  cursor: not-allowed;
  opacity: 0.5;
  box-shadow: none !important;
}

.interactive-social-wall__inline-spinner {
  display: inline-block;
  width: 11px;
  height: 11px;
  border: 1.5px solid rgba(104, 117, 139, 0.32);
  border-top-color: #50b83e;
  border-radius: 50%;
  animation: interactive-social-wall-spin 700ms linear infinite;
}

.interactive-social-wall__inline-spinner--light {
  margin-right: 6px;
  border-color: rgba(255, 255, 255, 0.42);
  border-top-color: #ffffff;
}

@keyframes interactive-social-wall-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 640px) {
  .interactive-social-wall__comment-form {
    position: relative;
    z-index: 2;
    flex: 0 0 auto;
    padding-bottom: calc(18px + env(safe-area-inset-bottom));
    background: #ffffff;
    border-top: 1px solid #e5e9ef;
    box-shadow: 0 -8px 20px rgba(15, 23, 42, 0.05);
  }

  .interactive-social-wall__comment-editor textarea {
    font-size: 16px;
  }
}

@media (max-width: 480px) {
  .interactive-social-wall__comment-form {
    padding-right: 14px;
    padding-bottom: calc(14px + env(safe-area-inset-bottom));
    padding-left: 14px;
  }

  .interactive-social-wall__comment-form-footer {
    align-items: flex-end;
    justify-content: flex-end;
    padding-left: 0;
  }
}
</style>
